import React, { useEffect, useRef } from 'react';
import Overlay from './Overlay';
import styles from './StreamVideo.module.css';

/**
 * Cloudflare Stream rendering, shared by the admin Media library and the parent
 * portal. A Stream file (from the API) looks like:
 *   { type:'video', streamUid, ready, error, width, height, iframeUrl, thumbnailUrl }
 * `iframeUrl` / `thumbnailUrl` carry a 1-hour signed token minted by the backend.
 *
 * Three pieces:
 *   <StreamVideo file />   inline player, sized by the clip's own aspect ratio so a
 *                          vertical phone video is tall-and-narrow, never cropped.
 *   <StreamThumb file />   poster frame + play badge for grid tiles.
 *   <VideoOverlay file />  fullscreen overlay player (Esc / backdrop closes).
 *
 * Until Stream finishes transcoding (`ready` false) both render a "Processing"
 * tile; the backend re-checks Stream on every list load and flips `ready`.
 */

const BRAND = '%23FF6B00'; // player accent (URI-encoded #FF6B00)

function playerSrc(file, autoplay) {
  const sep = file.iframeUrl.includes('?') ? '&' : '?';
  return `${file.iframeUrl}${sep}primaryColor=${BRAND}${autoplay ? '&autoplay=true' : ''}`;
}

function ratio(file) {
  const w = Number(file.width) > 0 ? Number(file.width) : 16;
  const h = Number(file.height) > 0 ? Number(file.height) : 9;
  return { w, h };
}

function StatusTile({ file, compact }) {
  if (file.error) {
    return (
      <div className={`${styles.tile} ${compact ? styles.tileCompact : ''}`}>
        <span className={styles.tileIcon}>!</span>
        <span>Video could not be processed</span>
        {!compact && <small>{file.error}</small>}
      </div>
    );
  }
  return (
    <div className={`${styles.tile} ${compact ? styles.tileCompact : ''}`}>
      <span className={styles.spinner} />
      <span>Processing video…</span>
      {!compact && <small>Usually ready within a few minutes. Refresh to check.</small>}
    </div>
  );
}

export default function StreamVideo({ file, maxHeight = 480, autoplay = false, className = '' }) {
  if (!file.ready || !file.iframeUrl) return <StatusTile file={file} />;
  const { w, h } = ratio(file);
  // Width is whichever is smaller: the container, or the width that makes the
  // clip exactly maxHeight tall at its own aspect ratio. aspect-ratio then
  // gives the height, so a 9:16 clip becomes e.g. 270x480, a 16:9 clip fills.
  const style = {
    width: `min(100%, calc(${maxHeight}px * ${w} / ${h}))`,
    aspectRatio: `${w} / ${h}`,
  };
  return (
    <div className={`${styles.frame} ${className}`} style={style}>
      <iframe
        src={playerSrc(file, autoplay)}
        title={file.filename || 'video'}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
      />
    </div>
  );
}

export function StreamThumb({ file, className = '', style, onClick }) {
  const ready = file.ready && file.thumbnailUrl;
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick && onClick(); }
  };
  return (
    <div
      className={`${styles.thumbWrap} ${className}`}
      style={style}
      onClick={ready ? onClick : undefined}
      onKeyDown={ready ? onKeyDown : undefined}
      tabIndex={ready ? 0 : undefined}
      role={ready ? 'button' : undefined}
      aria-label={ready ? `Play ${file.filename || 'video'}` : undefined}
    >
      {ready
        ? <img src={file.thumbnailUrl} alt={file.filename || 'video'} />
        : <StatusTile file={file} compact />}
      {ready && <span className={styles.playBadge}>▶</span>}
    </div>
  );
}

export function VideoOverlay({ file, caption, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    // Esc closes only while focus is outside the (cross-origin) player iframe;
    // the visible close button is the reliable control, so focus it on open
    // and hand focus back to whatever opened us on close.
    const opener = document.activeElement;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [onClose]);
  return (
    <Overlay className={styles.overlay} onClose={onClose} role="dialog" aria-modal="true">
      <button ref={closeRef} className={styles.close} onClick={onClose} aria-label="Close">✕</button>
      <div className={styles.overlayBody}>
        <StreamVideo file={file} autoplay maxHeight={Math.round(window.innerHeight * 0.82)} />
        {caption && <p className={styles.overlayCaption}>{caption}</p>}
      </div>
    </Overlay>
  );
}
